import {
  Dialog,
  FormDatePicker,
  FormFileInput,
  FormInput,
  FormSelect,
  FormTextarea,
  ImageCropper,
  Loader,
} from "@/components";
import useAddEdit from "./useAddEdit";
import { FormProvider } from "react-hook-form";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui";
import {
  COMPANY,
  companyOptions,
  DATE,
  DESCRIPTION,
  TITLE,
  TYPE,
  UPLOAD,
  YEAR,
} from "./consts";
import { X } from "lucide-react";
import { capitalize } from "@/lib/utils";
import moment from "moment";

const AddEdit = () => {
  const {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel,
    isAddPending,
    isMobile,
    uploadRef,
    isAddImagePending,
    isNewsMediaDataLoading,
    action,
    isUpdatePending,
    isEdit,
    onFileChange,
    openCropper,
    onCloseCropper,
    imageSrc,
    orgImageName,
    onViewImage,
    openImageViewer,
    setOpenImageViewer,
    isImageLoading,
    setOpenCropper,
    showLink,
  } = useAddEdit();
  const { errors } = form.formState;

  return (
    <>
      <Drawer open={true} direction={isMobile ? "bottom" : "right"}>
        <DrawerContent className="px-6 lg:min-w-[500px] xl:min-w-[730px]">
          <DrawerHeader className="px-0">
            <div className="flex items-center justify-between">
              <DrawerTitle className="text-[20px]">
                {capitalize(action || "")}
              </DrawerTitle>
              <X
                onClick={onCancel}
                className="cursor-pointer stroke-g-300 hover:stroke-g-400"
              />
            </div>
          </DrawerHeader>
          {isNewsMediaDataLoading ? (
            <Loader />
          ) : (
            <FormProvider {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
                <div className="bg-white rounded-lg py-[25px] max-h-[70vh] lg:max-h-[95vh] overflow-y-auto pb-[100px] px-1">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px]">
                    <FormInput
                      name={TITLE}
                      control={form.control}
                      label="Title"
                      placeholder="Please enter"
                      error={errors[TITLE]?.message}
                      isRequired={requiredFields.includes(TITLE)}
                      disabled={isView}
                    />
                    <FormDatePicker
                      name={DATE}
                      control={form.control}
                      label="Date"
                      placeholder="Please enter"
                      error={errors[DATE]?.message}
                      isRequired={requiredFields.includes(DATE)}
                      disabled={isView}
                      onChange={(value: any) => {
                        form.setValue(YEAR, moment(value).format("YYYY"));
                      }}
                    />
                    <FormInput
                      name={YEAR}
                      control={form.control}
                      label="Year"
                      placeholder="Please enter"
                      error={errors[YEAR]?.message}
                      isRequired={requiredFields.includes(YEAR)}
                      disabled
                    />
                    <FormInput
                      name={TYPE}
                      control={form.control}
                      label="Type"
                      placeholder="Please enter"
                      error={errors[TYPE]?.message}
                      isRequired={requiredFields.includes(TYPE)}
                      disabled={isView}
                    />
                    <FormSelect
                      name={COMPANY}
                      control={form.control}
                      label="Company"
                      placeholder="Please enter"
                      error={errors[COMPANY]?.message}
                      isRequired={requiredFields.includes(COMPANY)}
                      disabled={isView}
                      options={companyOptions}
                    />
                    <div>
                      <FormFileInput
                        fileInputRef={uploadRef}
                        name={UPLOAD}
                        control={form.control}
                        label="Upload (dimension 932x486)"
                        placeholder="Please enter"
                        error={errors[UPLOAD]?.message}
                        isRequired
                        disabled={isView}
                        accept="image/jpeg,image/png,image/jpg"
                        minSize={500}
                        maxSize={600}
                        uploadFileErrorMsg="File size should be between 500 KB and 600 KB (JPEG, JPG, PNG)"
                        onChange={onFileChange}
                      />
                      {showLink ? (
                        <Button
                          type="button"
                          variant="link"
                          onClick={onViewImage}
                        >
                          View Image
                        </Button>
                      ) : (
                        <></>
                      )}
                    </div>
                    <ImageCropper
                      onCancel={onCloseCropper}
                      open={openCropper}
                      setOpen={setOpenCropper}
                      imageSrc={imageSrc}
                      areaPixel={{ height: 729, width: 1398 }}
                      quality={0.95}
                      onCropDone={(blob) => {
                        const file = new File([blob], orgImageName, {
                          type: blob.type,
                        });
                        form.setValue(UPLOAD, file);
                      }}
                    />
                    <div className="md:col-span-2">
                      <FormTextarea
                        name={DESCRIPTION}
                        control={form.control}
                        label={
                          <span>
                            News Description{" "}
                            <span className="text-[10px]">
                              (max 2000 Character)
                            </span>
                          </span>
                        }
                        placeholder="Please enter"
                        error={errors[DESCRIPTION]?.message}
                        isRequired={requiredFields.includes(DESCRIPTION)}
                        disabled={isView}
                        className="h-[200px]"
                        maxLength={2000}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex flex-row justify-end gap-3 py-3 absolute bottom-0 w-full bg-white pb-[25px] z-[3]">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={onCancel}
                    disabled={
                      isAddPending || isAddImagePending || isUpdatePending
                    }
                  >
                    Cancel
                  </Button>
                  {isView ? (
                    <></>
                  ) : (
                    <Button
                      type="submit"
                      disabled={
                        isAddPending || isAddImagePending || isUpdatePending
                      }
                    >
                      {isAddPending ||
                        isAddImagePending ||
                        (isUpdatePending && <Loader variant="secondary" />)}
                      {isEdit ? "Update" : "Submit"}
                    </Button>
                  )}
                </div>
              </form>
            </FormProvider>
          )}
        </DrawerContent>
      </Drawer>
      <Dialog
        open={openImageViewer}
        setOpen={setOpenImageViewer}
        title={<h2 className="text-2xl font-bold">View Image</h2>}
        hideFooter
        contentClassName="h-[486px] w-[932px]"
      >
        <div className="relative bg-white">
          {isImageLoading ? <Loader /> : <img src={imageSrc} />}
        </div>
      </Dialog>
    </>
  );
};

export default AddEdit;
